import { useEffect, useLayoutEffect, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import {
  ProjectManagementValueObjectsBudgetStrategyTypeEnum,
  ProjectManagementValueObjectsStatusTypesEnum,
  SubprojectSidepanelDetailsDocument,
  SubprojectSidepanelDetailsQuery,
  SubprojectsDataCollectionDocument,
  UpdateSubprojectDocument,
  UpdateSubprojectMutation,
} from 'generated/resources/graphql'

import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import { Result } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import SubprojectsPanel from 'modules/project_management/components/tables/SubprojectsDataCollection/SubprojectsPanel'
import SubprojectsSettingsTab from 'modules/project_management/components/tables/SubprojectsDataCollection/SubprojectsSettingsTab'
import {
  SubprojectBasicDetails,
  SubprojectFinancialDetails,
  SubprojectsSettingsFormValues,
} from 'modules/project_management/components/tables/SubprojectsDataCollection/types'
import {
  useCreateBudgetStrategy,
  useUpdateBudgetStrategy,
} from 'modules/project_management/hooks/planningMutations/budgetStrategyHooks'
import {
  getDecimalHoursFromMinutes,
  getMinutesFromDecimalHours,
} from 'modules/project_management/lib/formatHours'
import { ISubprojectsPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/types'

type SubprojectNode = NonNullable<
  SubprojectSidepanelDetailsQuery['projectManagement']['subprojectsConnection']['nodes'][number]
>

export type { SubprojectBasicDetails }

export interface SubprojectSidepanelProps {
  subprojectId: string
  subprojectData: ISubprojectsPanelData
  onClose: () => void
  showSubprojectsSidepanelOne: boolean
  projectId: string
  projectCurrency: string
  canSeeLaborCosts: boolean
  subprojectEntityFeatureEnabled: boolean
  companyId: string
  includeCost: boolean
  projectDelegated?: boolean
  isOwnerOrAdmin: boolean
}

const TABSIDS = {
  HOURS: 'hours',
  SETTINGS: 'settings',
} as const

const isValidStatus = (status: string): status is ProjectManagementValueObjectsStatusTypesEnum => {
  return ['active', 'closed', 'draft', 'processing'].includes(status)
}

const isValidBudgetStrategyType = (
  type: string | undefined | null
): type is ProjectManagementValueObjectsBudgetStrategyTypeEnum => {
  if (!type) return false
  return ['project_fixed_cost', 'total_budget', 'without_budget'].includes(type)
}

const toStatusEnum = (status?: string | null): ProjectManagementValueObjectsStatusTypesEnum => {
  if (status && isValidStatus(status)) {
    return status
  }
  return 'active'
}

export const SubprojectsSidepanelTabs = ({
  subprojectId: subprojectId,
  subprojectData,
  onClose,
  showSubprojectsSidepanelOne,
  projectId,
  projectCurrency,
  canSeeLaborCosts,
  subprojectEntityFeatureEnabled,
  companyId,
  includeCost,
  projectDelegated = false,
  isOwnerOrAdmin,
}: SubprojectSidepanelProps) => {
  const client = useApolloClient()
  const [activeTab, setActiveTab] = useState<(typeof TABSIDS)[keyof typeof TABSIDS]>(TABSIDS.HOURS)
  useLayoutEffect(() => {
    setActiveTab(TABSIDS.HOURS)
  }, [showSubprojectsSidepanelOne, subprojectId])

  const { loading, data } = useQuery(SubprojectSidepanelDetailsDocument, {
    variables: {
      subprojectId: [subprojectId],
      includeCost: includeCost,
    },
    fetchPolicy: 'no-cache',
    skip: subprojectId === '0',
  })

  const [updateSubproject] = useMutation(UpdateSubprojectDocument, {
    extractorFn: (mutationData: UpdateSubprojectMutation) =>
      mutationData.projectManagementMutations.updateProjectmanagementSubproject,
    awaitRefetchQueries: true,
    refetchQueries: [SubprojectSidepanelDetailsDocument],
  })

  const refetchSubprojectsDataCollection = () => {
    void client.refetchQueries({
      include: [SubprojectsDataCollectionDocument],
    })
  }

  const { createBudgetStrategy } = useCreateBudgetStrategy(companyId)
  const { updateBudgetStrategy } = useUpdateBudgetStrategy()

  interface BudgetStrategyParams {
    budgetStrategyType: ProjectManagementValueObjectsBudgetStrategyTypeEnum
    plannedCents: number | undefined
    plannedMinutes: number | undefined
    fixedCostCents: number | undefined
    feeAmountCents: number | undefined
    isBillable: boolean | undefined
  }

  const hasAnyFinancialDetails = (financialDetails: SubprojectFinancialDetails): boolean =>
    financialDetails.estimatedCost !== undefined ||
    financialDetails.fixedCost !== undefined ||
    financialDetails.feeAmount !== undefined ||
    financialDetails.estimatedHours !== undefined

  const convertFinancialDetails = (financialDetails: SubprojectFinancialDetails) => ({
    plannedCents: financialDetails.estimatedCost
      ? Math.round(financialDetails.estimatedCost * 100)
      : undefined,
    fixedCostCents: financialDetails.fixedCost
      ? Math.round(financialDetails.fixedCost * 100)
      : undefined,
    feeAmountCents:
      financialDetails.feeAmount && financialDetails.isBillable
        ? Math.round(financialDetails.feeAmount * 100)
        : undefined,
    plannedMinutes: financialDetails.estimatedHours
      ? getMinutesFromDecimalHours(financialDetails.estimatedHours)
      : undefined,
  })

  const updateExistingBudgetStrategy = async (
    strategyId: string,
    params: BudgetStrategyParams
  ): Promise<Result<unknown>> => {
    const response = await updateBudgetStrategy({
      id: strategyId,
      ...params,
    })
    return handleResult(response, () => undefined)
  }

  const createNewBudgetStrategy = async (
    params: BudgetStrategyParams
  ): Promise<Result<unknown>> => {
    const response = await createBudgetStrategy({
      projectId,
      delegated: false, // Subprojects don't delegate - they receive delegation from projects
      subprojectId: subprojectId,
      ...params,
    })
    return handleResult(response, () => undefined)
  }

  const getEmptyFormValues = (): SubprojectsSettingsFormValues => ({
    basicDetails: {
      name: undefined,
      startDate: undefined,
      dueDate: undefined,
      status: 'active',
      code: undefined,
      description: undefined,
    },
    financialDetails: {
      estimatedCost: undefined,
      fixedCost: undefined,
      estimatedHours: undefined,
      feeAmount: undefined,
      isBillable: false,
    },
  })

  const getFormValuesFromSubproject = (
    subproject: SubprojectNode
  ): SubprojectsSettingsFormValues => ({
    basicDetails: {
      name: subproject.name || undefined,
      startDate: subproject.startDate || undefined,
      dueDate: subproject.dueDate || undefined,
      status: toStatusEnum(subproject.status),
      code: subproject.code || undefined,
      description: subproject.description || undefined,
    },
    financialDetails: {
      estimatedCost: subproject.budgetStrategy?.plannedCents
        ? subproject.budgetStrategy?.plannedCents / 100
        : undefined,
      fixedCost: subproject.fixedCostCents ? subproject.fixedCostCents / 100 : undefined,
      estimatedHours: subproject.budgetStrategy?.plannedMinutes
        ? getDecimalHoursFromMinutes(subproject.budgetStrategy?.plannedMinutes)
        : undefined,
      feeAmount:
        subproject.budgetStrategy?.feeAmountCents && subproject.isBillable
          ? subproject.budgetStrategy?.feeAmountCents / 100
          : undefined,
      isBillable: subproject.isBillable ?? false,
    },
  })

  const subprojectNode = data?.projectManagement?.subprojectsConnection?.nodes[0]
  const subprojectDetails = {
    subprojectName: subprojectNode?.name || undefined,
    subprojectId: subprojectId,
    description: subprojectNode?.description || undefined,
    status: toStatusEnum(subprojectNode?.status),
    code: subprojectNode?.code || undefined,
    startDate: subprojectNode?.startDate || undefined,
    dueDate: subprojectNode?.dueDate || undefined,
  }

  const currency = subprojectNode?.project?.legalEntity?.currency || undefined
  const budgetType = subprojectNode?.project?.budgetStrategy?.budgetType || undefined
  const delegated =
    subprojectId === '0'
      ? projectDelegated
      : subprojectNode?.project?.budgetStrategy?.delegated || false

  const sidepanelTitle =
    subprojectId !== '0'
      ? subprojectData?.subprojectName
      : i18n.t('project_management.subprojects.without_subproject_alerts.not_delegated.title')

  const form = useForm<SubprojectsSettingsFormValues>({
    defaultValues: {
      ...(subprojectNode ? getFormValuesFromSubproject(subprojectNode) : getEmptyFormValues()),
    },
    validate: (formData) => {
      const errors: FormErrors<SubprojectBasicDetails> = {}

      if (!formData.basicDetails.status) {
        errors.status = i18n.t('project_management.modals.error_messages.mandatory')
      }

      const numericNonNegativeFields = [
        'estimatedCost',
        'fixedCost',
        'feeAmount',
        'estimatedHours',
      ] as const

      const financialErrors: Record<string, string> = {}
      for (const field of numericNonNegativeFields) {
        const value = formData?.financialDetails?.[field]
        if (!!value && value < 0) {
          financialErrors[field] = i18n.t(
            'project_management.modals.error_messages.cannot_be_negative'
          )
        }
        if (value !== undefined && value !== null && isNaN(Number(value))) {
          financialErrors[field] = i18n.t(
            'project_management.modals.error_messages.must_be_numeric'
          )
        }
      }

      if (Object.keys(financialErrors).length > 0) {
        return { ...errors, financialDetails: financialErrors }
      }

      return errors
    },
    onSubmit: async (formData): Promise<Result<unknown>> => {
      // 1. Update subproject basic details
      const subprojectResponse = await updateSubproject({
        variables: {
          id: subprojectId,
          name: formData.basicDetails.name,
          startDate: formData.basicDetails.startDate,
          dueDate: formData.basicDetails.dueDate,
          status: formData.basicDetails.status,
          code: formData.basicDetails.code,
          description: formData.basicDetails.description,
          fixedCostCents:
            formData.financialDetails.fixedCost && budgetType === 'without_budget'
              ? Math.round(formData.financialDetails.fixedCost * 100)
              : undefined,
          isBillable: budgetType === 'without_budget' && formData.financialDetails.isBillable,
        },
        awaitRefetchQueries: true,
      })
      const subprojectResult = handleResult(subprojectResponse, () => undefined)

      // 2. Handle budget strategy
      let budgetResult: Result<unknown> | null = null

      if (hasAnyFinancialDetails(formData.financialDetails)) {
        const projectStrategyType = budgetType

        const existingStrategyForSubproject = subprojectNode?.budgetStrategy

        if (
          isValidBudgetStrategyType(projectStrategyType) &&
          projectStrategyType !== 'without_budget'
        ) {
          const budgetParams: BudgetStrategyParams = {
            budgetStrategyType: projectStrategyType,
            ...convertFinancialDetails(formData.financialDetails),
            isBillable: formData.financialDetails.isBillable,
          }

          budgetResult = existingStrategyForSubproject
            ? await updateExistingBudgetStrategy(existingStrategyForSubproject.id, budgetParams)
            : await createNewBudgetStrategy(budgetParams)
        }
      }

      // Refetch after ALL mutations have completed (including budget strategy)
      refetchSubprojectsDataCollection()

      // Show appropriate feedback based on results
      const allSucceeded = subprojectResult.success && (!budgetResult || budgetResult.success)

      if (allSucceeded) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        track(trackingEvents.projectManagement.subprojects.clickOnSaveUpdatePhase, {
          isBillable: formData.financialDetails.isBillable,
        })
        onClose()
      } else {
        // Show error - at least one failed
        app.setFlash({
          type: 'wrong',
          message: i18n.t('project_management.subprojects.failed_undetermined_mutation'),
        })
      }

      return subprojectResult
    },
  })

  useEffect(() => {
    form.setValues(getEmptyFormValues())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subprojectId])

  useEffect(() => {
    if (subprojectNode) {
      form.setValues(getFormValuesFromSubproject(subprojectNode))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleSave = () => {
    form.handleSubmit()
  }

  const handleBasicDetailsChanged = (
    fieldName: keyof SubprojectBasicDetails,
    fieldValue: string | undefined | boolean
  ) => {
    form.setValues({
      ...form.values,
      basicDetails: { ...form.values.basicDetails, [fieldName]: fieldValue },
    })
  }

  const handleFinancialDetailsChanged = (
    fieldName: keyof SubprojectFinancialDetails,
    fieldValue: number | boolean | undefined
  ) => {
    form.setValues({
      ...form.values,
      financialDetails: { ...form.values.financialDetails, [fieldName]: fieldValue },
    })
  }

  const requiredFieldsFilledCheck = () => {
    // Only require financial fields when they are visible (delegated); otherwise the user cannot fill them
    if (delegated) {
      if (
        budgetType === 'project_fixed_cost' &&
        form.values.financialDetails.estimatedCost === undefined
      ) {
        return false
      }
    }
    if (
      form.values.basicDetails.name === undefined ||
      form.values.basicDetails.status === undefined
    ) {
      return false
    }
    return true
  }

  return subprojectEntityFeatureEnabled ? (
    <F0Dialog
      isOpen={showSubprojectsSidepanelOne}
      onClose={onClose}
      position="right"
      title={sidepanelTitle || i18n.t('ats.job_posting_settings.smart_actions.phase')}
      primaryAction={
        activeTab === TABSIDS.SETTINGS
          ? {
              label: i18n.t('common.save'),
              onClick: handleSave,
              disabled: loading || !form.isValid || !requiredFieldsFilledCheck(),
            }
          : undefined
      }
      secondaryAction={
        activeTab === TABSIDS.SETTINGS
          ? {
              label: i18n.t('common.cancel'),
              onClick: onClose,
            }
          : undefined
      }
      key={subprojectId}
      activeTabId={activeTab}
      tabs={[
        ...(subprojectId !== '0'
          ? [
              {
                id: TABSIDS.HOURS,
                label: i18n.t('common.activity'),
                onClick: () => {
                  setActiveTab(TABSIDS.HOURS)
                  track(trackingEvents.projectManagement.subprojects.clickOnActivityTab)
                },
              },
              {
                id: TABSIDS.SETTINGS,
                label: i18n.t('common.settings'),
                onClick: () => setActiveTab(TABSIDS.SETTINGS),
              },
            ]
          : []),
      ]}
    >
      {loading ? (
        <Box padding="s40" width="full" alignItems="center" flexGrow>
          <Spinner />
        </Box>
      ) : (
        <>
          {activeTab === TABSIDS.SETTINGS && (
            <SubprojectsSettingsTab
              subprojectId={subprojectId}
              subprojectData={subprojectData}
              formValues={form.values}
              formErrors={form.errors}
              onBasicDetailsChange={handleBasicDetailsChanged}
              onFinancialDetailsChange={handleFinancialDetailsChanged}
              subprojectDetails={subprojectDetails}
              currency={currency}
              budgetType={budgetType}
              delegated={delegated}
              projectId={projectId}
              isOwnerOrAdmin={isOwnerOrAdmin}
            />
          )}

          {activeTab === TABSIDS.HOURS && (
            // TO DO: Rename to activity tab once we get rid of the old sidepanel
            <SubprojectsPanel
              onClose={onClose}
              showSubprojectsPanel={showSubprojectsSidepanelOne}
              projectId={projectId}
              projectCurrency={projectCurrency}
              canSeeLaborCosts={canSeeLaborCosts}
              subprojectPanelData={subprojectData}
              subprojectEntityFeatureEnabled={subprojectEntityFeatureEnabled}
              delegated={delegated}
            />
          )}
        </>
      )}
    </F0Dialog>
  ) : (
    <SubprojectsPanel
      onClose={onClose}
      showSubprojectsPanel={showSubprojectsSidepanelOne}
      projectId={projectId}
      projectCurrency={projectCurrency}
      canSeeLaborCosts={canSeeLaborCosts}
      subprojectPanelData={subprojectData}
      subprojectEntityFeatureEnabled={subprojectEntityFeatureEnabled}
      delegated={delegated}
    />
  )
}
